import { useEffect, useRef, useState } from "react";
import { Minus, Plus, BadgePercent, Lock, ArrowLeft } from "lucide-react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { stripePromise } from "@/lib/stripe";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/landing/Reveal";
import { SERVICE_AREAS, MARICOPA_ROUTE_DAYS, MARICOPA_ROUTE_LABEL, nextMaricopaRouteDates, fmtRouteDate } from "@/lib/site";

const TIME_WINDOWS = ["8–10 AM", "10 AM–12 PM", "12–2 PM", "2–4 PM", "4–6 PM"];
const SERVICE_OPTIONS = [
  "Comforters & Bedding",
  "Dry Cleaning",
  "Wash & Fold",
  "Household Linens & Curtains",
  "Alterations & Repairs",
  "Leather Care",
  "Wedding Dress Preservation",
];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-brand-navy placeholder:text-slate-400 focus:border-brand-blue focus:outline-none";

// Best-effort beacon so silent client-side failures show up in server logs.
const reportClientError = (where, message, phone) => {
  api.post("/public/client-error", { where, message: String(message).slice(0, 280), phone: phone || null }).catch(() => {});
};

// Phase 2: secure card entry — required before the pickup is booked.
const CardFields = ({ form, onBack, onBooked }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [elementReady, setElementReady] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const rootRef = useRef(null);

  // When the card step swaps in, bring it into view — otherwise the customer
  // is left staring at whatever was below the (longer) details form.
  useEffect(() => {
    rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // If the secure card form never mounts (blocked script, bad connection),
  // tell the customer instead of leaving a dead button.
  useEffect(() => {
    const t = setTimeout(() => setLoadTimedOut(true), 15000);
    return () => clearTimeout(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !elementReady) {
      setError("The secure card form isn't ready yet — give it a few seconds and try again. If it never appears, please call 480-940-6023.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { error: stripeErr, setupIntent } = await stripe.confirmSetup({ elements, redirect: "if_required" });
      if (stripeErr) {
        setError(stripeErr.message || "Card could not be saved — check it and try again.");
        return;
      }
      await api.post("/public/booking-card-confirm", {
        phone: form.phone,
        payment_method_id: setupIntent.payment_method,
      });
      await onBooked();
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't finish — please call 480-940-6023 and we'll book you by phone.");
      reportClientError("booking-card-confirm", err.response ? `${err.response.status}: ${err.response.data?.detail}` : err.message, form.phone);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form ref={rootRef} onSubmit={submit} className="mt-12 scroll-mt-24 rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-brand-blue/10 sm:p-10" data-testid="booking-card-phase">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue">Almost done</p>
      <h3 className="mt-2 font-display text-xl font-extrabold text-brand-navy sm:text-2xl">
        Last step — your card{form.name ? `, ${form.name.split(" ")[0]}` : ""}
      </h3>
      <p className="mt-1 text-sm font-semibold text-slate-500">
        Pickup: {form.pickup_date} · {form.time_window}
      </p>
      <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <p className="text-sm font-semibold leading-relaxed text-emerald-800">
          Your card is only charged once your clothes are done and scheduled for drop-off.
          Pickup &amp; delivery are always free, and Stripe secures your card — we never see the number.
        </p>
      </div>
      <div className="mt-5">
        <PaymentElement onReady={() => setElementReady(true)} />
        {!elementReady && !loadTimedOut && (
          <p data-testid="booking-card-loading" className="mt-3 text-center text-xs font-semibold text-slate-400">
            Loading the secure card form…
          </p>
        )}
        {loadTimedOut && !elementReady && (
          <p data-testid="booking-card-load-failed" className="mt-3 rounded-xl bg-amber-50 p-3 text-center text-sm font-semibold text-amber-700">
            The card form is taking too long to load — please refresh the page and try again, or call 480-940-6023 and we&apos;ll book you by phone.
          </p>
        )}
      </div>
      {error && <p data-testid="booking-card-error" className="mt-3 text-sm font-semibold text-rose-500">{error}</p>}
      <button
        data-testid="booking-card-submit"
        type="submit"
        disabled={saving || !stripe}
        className="mt-5 w-full rounded-2xl bg-brand-blue px-6 py-4 font-display text-sm font-extrabold uppercase tracking-widest text-white shadow-lg shadow-brand-blue/30 transition-transform hover:scale-[1.01] disabled:opacity-60"
      >
        {saving ? "Securing your card…" : "Save card & book my pickup"}
      </button>
      <button
        data-testid="booking-card-back"
        type="button"
        onClick={onBack}
        className="mt-3 flex w-full items-center justify-center gap-1.5 text-xs font-bold text-slate-400 hover:text-brand-blue"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to my details
      </button>
    </form>
  );
};


export const Booking = ({ presetCity } = {}) => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const tomorrow = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: presetCity || "Chandler",
    pickup_date: "",
    time_window: TIME_WINDOWS[0],
    notes: "",
  });
  const [services, setServices] = useState(["Comforters & Bedding"]);
  const [comforters, setComforters] = useState(2);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const handler = (e) => setForm((prev) => ({ ...prev, city: e.detail }));
    window.addEventListener("oxford:set-city", handler);
    return () => window.removeEventListener("oxford:set-city", handler);
  }, []);
  const [rebookOpen, setRebookOpen] = useState(false);
  const [rebookName, setRebookName] = useState("");
  const [rebookPhone, setRebookPhone] = useState("");
  const [rebookState, setRebookState] = useState(null);
  const [rebookLoading, setRebookLoading] = useState(false);

  const findLastOrder = async (e) => {
    e.preventDefault();
    setRebookLoading(true);
    setRebookState(null);
    try {
      const { data } = await api.post("/bookings/lookup", { name: rebookName, phone: rebookPhone });
      if (data.found) {
        const last = data.last;
        setForm((prev) => ({ ...prev, name: rebookName, phone: rebookPhone, address: last.address, city: last.city, time_window: last.time_window }));
        setServices(last.services);
        setComforters(last.comforters);
        setRebookState("found");
        toast.success("Found your last order — details prefilled. Just pick a date!");
      } else {
        setRebookState("missing");
      }
    } catch {
      setRebookState("missing");
    } finally {
      setRebookLoading(false);
    }
  };

  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const [phase, setPhase] = useState("details"); // "details" | "card"
  const [cardSetup, setCardSetup] = useState(null); // { clientSecret }

  const finalizeBooking = async () => {
    const { data: created } = await api.post("/bookings", {
      ...form,
      email: form.email || null,
      notes: form.notes || null,
      services,
      comforters,
    });
    window.location.href = `/thank-you?booking=${created.id}`;
  };

  // Phase 1: details -> check for card on file -> card phase or straight to booking
  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (services.length === 0) {
      setFormError("Pick at least one service.");
      toast.error("Pick at least one service.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/public/booking-card-setup", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
      });
      if (data.has_card) {
        toast.success(`We have your card ending ${data.card.last4} — booking now.`);
        await finalizeBooking();
        return;
      }
      setCardSetup({ clientSecret: data.client_secret });
      setPhase("card");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? `Please double-check: ${detail.map((x) => x.loc?.slice(-1)[0]).join(", ")}`
          : "Couldn't start your booking — please call 480-940-6023.";
      setFormError(msg);
      toast.error(msg);
      reportClientError("booking-setup", err.response ? `${err.response.status}: ${msg}` : err.message, form.phone);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="relative overflow-hidden bg-white py-24 lg:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(37,99,235,0.08),rgba(245,158,11,0.05)_50%,transparent_100%)]" />

      <div className="relative mx-auto max-w-4xl px-4 sm:px-6">
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue sm:text-sm">Book a pickup</p>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
            Your driver is one form away
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
            Request a pickup and we&apos;ll call to confirm. Seasonal special pricing applies
            automatically when you add 2+ comforters.
          </p>
        </Reveal>

        <Reveal delay={0.05} className="mt-8 text-center">
          {!rebookOpen ? (
            <button
              data-testid="rebook-toggle"
              type="button"
              onClick={() => setRebookOpen(true)}
              className="text-sm font-bold text-brand-blue underline decoration-fall-amber decoration-2 underline-offset-4 transition-colors hover:text-brand-blue-hover"
            >
              Booked with us before? Rebook your usual in one tap →
            </button>
          ) : (
            <form onSubmit={findLastOrder} className="mx-auto flex max-w-xl flex-col items-stretch gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center">
              <input data-testid="rebook-name-input" required minLength={2} value={rebookName} onChange={(e) => setRebookName(e.target.value)} placeholder="Name on last order" className={inputCls} />
              <input data-testid="rebook-phone-input" required minLength={7} value={rebookPhone} onChange={(e) => setRebookPhone(e.target.value)} placeholder="Phone number" className={inputCls} />
              <button data-testid="rebook-find-button" type="submit" disabled={rebookLoading} className="shrink-0 rounded-xl bg-brand-navy px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-navy-light disabled:opacity-60">
                {rebookLoading ? "Finding…" : "Find it"}
              </button>
            </form>
          )}
          {rebookState === "found" && (
            <p data-testid="rebook-found-note" className="mt-3 text-sm font-semibold text-emerald-600">
              Your usual is prefilled below — just pick a pickup date and send it.
            </p>
          )}
          {rebookState === "missing" && (
            <p data-testid="rebook-missing-note" className="mt-3 text-sm font-semibold text-rose-500">
              No past order found for that name + phone.
            </p>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          {phase === "card" ? (
            cardSetup?.clientSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret: cardSetup.clientSecret, paymentMethodCreation: "manual" }}>
                <CardFields form={form} onBack={() => setPhase("details")} onBooked={finalizeBooking} />
              </Elements>
            ) : (
              <div className="mt-12 rounded-3xl border border-slate-200 bg-white p-10 text-center text-sm font-semibold text-slate-400 shadow-2xl shadow-brand-blue/10">
                Loading secure card form…
              </div>
            )
          ) : (
          <form
            onSubmit={submit}
            className="mt-12 rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl shadow-brand-blue/10 sm:p-10"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="booking-form-name-input" required minLength={2} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <input data-testid="booking-form-phone-input" required minLength={7} placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
              <input data-testid="booking-form-email-input" type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              <select data-testid="booking-form-city-select" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls}>
                {SERVICE_AREAS.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <input data-testid="booking-form-address-input" required minLength={5} placeholder="Street address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={`${inputCls} sm:col-span-2`} />
              <input data-testid="booking-form-date-select" type="date" required min={tomorrow} value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} className={inputCls} />
              <select data-testid="booking-form-time-select" value={form.time_window} onChange={(e) => setForm({ ...form, time_window: e.target.value })} className={inputCls}>
                {TIME_WINDOWS.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>

            {form.city === "Maricopa" && (
              <div data-testid="maricopa-route-days" className="mt-5 rounded-2xl border border-fall-border bg-fall-bg p-5">
                <p className="text-sm font-bold text-brand-navy">
                  Maricopa routes run {MARICOPA_ROUTE_LABEL} — tap a route day:
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {nextMaricopaRouteDates(4).map((d) => {
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    const active = form.pickup_date === iso;
                    return (
                      <button
                        key={iso}
                        type="button"
                        data-testid={`maricopa-route-day-${iso}`}
                        onClick={() => setForm({ ...form, pickup_date: iso })}
                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                          active
                            ? "border-fall-orange bg-fall-orange text-white"
                            : "border-fall-border bg-white text-slate-600 hover:border-fall-orange"
                        }`}
                      >
                        {fmtRouteDate(d)}
                      </button>
                    );
                  })}
                </div>
                {form.pickup_date &&
                  !MARICOPA_ROUTE_DAYS.includes(new Date(`${form.pickup_date}T12:00:00`).getDay()) && (
                    <p data-testid="maricopa-off-route-warning" className="mt-3 text-xs font-semibold text-fall-orange">
                      That&apos;s not a Maricopa route day — no problem, we&apos;ll confirm your exact day by phone.
                    </p>
                  )}
              </div>
            )}

            <p className="mt-7 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Services</p>
            <div className="mt-3 flex flex-wrap gap-2.5">
              {SERVICE_OPTIONS.map((s) => {
                const active = services.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`booking-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => toggleService(s)}
                    className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                      active
                        ? "border-brand-blue bg-brand-blue text-white"
                        : "border-slate-200 bg-slate-50 text-slate-600 hover:border-brand-blue/50"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>

            <div className="mt-7 flex flex-col items-start justify-between gap-4 rounded-2xl border border-fall-border bg-fall-bg p-5 sm:flex-row sm:items-center">
              <div>
                <p className="flex items-center gap-2 text-sm font-bold text-brand-navy">
                  <BadgePercent className="h-4 w-4 text-fall-orange" />
                  Comforters &amp; blankets
                </p>
                <p data-testid="price-calculator-total-display" className="mt-1 text-sm font-semibold text-fall-orange">
                  {comforters === 0
                    ? "Add comforters for Fall Special pricing"
                    : comforters === 1
                      ? "$25 · add one more for $19.99 each"
                      : `$19.99 each · ${comforters} = $${(comforters * 19.99).toFixed(2)} — Special applied`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" data-testid="price-calculator-comforter-minus" onClick={() => setComforters(Math.max(0, comforters - 1))} className="flex h-10 w-10 items-center justify-center rounded-full border border-fall-amber bg-white text-brand-navy transition-transform hover:scale-105">
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-8 text-center font-display text-2xl font-extrabold text-brand-navy">{comforters}</span>
                <button type="button" data-testid="price-calculator-comforter-plus" onClick={() => setComforters(Math.min(20, comforters + 1))} className="flex h-10 w-10 items-center justify-center rounded-full bg-fall-amber text-brand-navy transition-transform hover:scale-105">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <textarea
              data-testid="booking-form-notes-input"
              rows={3}
              placeholder="Notes for the driver (gate code, porch spot, etc.)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={`${inputCls} mt-6 resize-none`}
            />

            <button
              data-testid="booking-form-submit-button"
              type="submit"
              disabled={sending}
              onClick={(e) => {
                // If native validation stops the submit (no request fires),
                // beacon which field blocked it so failures aren't invisible.
                const f = e.currentTarget.form;
                if (f && !f.checkValidity()) {
                  const bad = [...f.elements].find((el) => el.willValidate && !el.checkValidity());
                  reportClientError(
                    "booking-validate",
                    bad ? `${bad.dataset.testid || bad.placeholder || "field"}: ${bad.validationMessage}` : "form invalid",
                    form.phone
                  );
                }
              }}
              className="mt-7 w-full rounded-2xl bg-brand-blue px-6 py-4 font-display text-sm font-extrabold uppercase tracking-widest text-white shadow-lg shadow-brand-blue/30 transition-transform hover:scale-[1.01] disabled:opacity-60"
            >
              {sending ? "Checking…" : "Continue — add my card"}
            </button>
            {formError && (
              <p data-testid="booking-form-error" className="mt-3 rounded-xl bg-rose-50 p-3 text-center text-sm font-semibold text-rose-600">
                {formError}
              </p>
            )}
            <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs font-semibold text-slate-400">
              <Lock className="h-3.5 w-3.5 text-emerald-600" />
              A card is required to book — it&apos;s only charged once your clothes are done &amp; scheduled for drop-off.
            </p>
          </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
